/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import { logger } from '@getaccept/lib-shared/src/logger';
import type {
  Channel,
  ConnectionState,
  PusherEventTypes,
  SubscribePayload,
} from '@getaccept/pusher';
import { PusherClient as PusherClientOriginal } from '@getaccept/pusher';
import bugsnagClient from '../bugsnag';

/**
 * Resilient wrapper around PusherClientOriginal with  error handling.
 * All methods are wrapped in try-catch blocks to prevent Pusher failures from crashing the application.
 */
export class PusherClient {
  pusher: PusherClientOriginal;
  constructor(baseUrl: string) {
    this.pusher = new PusherClientOriginal(baseUrl);
  }

  async connect(token: string) {
    try {
      return this.pusher.connect(token);
    } catch (error) {
      logger.error('Failed to connect to Pusher:', error);
      bugsnagClient?.notify(error);
    }
  }

  updateToken(token: string) {
    return this.pusher.updateToken(token);
  }

  async subscribe(payload: SubscribePayload) {
    try {
      return await this.pusher.subscribe(payload);
    } catch (error) {
      logger.error('Failed to subscribe to Pusher:', error);
      bugsnagClient?.notify(error);
    }
  }

  listen(channel: Channel, event: PusherEventTypes, handler: Function) {
    try {
      return this.pusher.listen(channel, event, handler);
    } catch (error) {
      logger.error('Failed to listen to Pusher:', error);
      bugsnagClient?.notify(error);
    }
  }

  listenGlobal(channel: Channel, handler: Function) {
    try {
      return this.pusher.listenGlobal(channel, handler);
    } catch (error) {
      logger.error('Failed to listen globally to Pusher:', error);
      bugsnagClient?.notify(error);
    }
  }

  connectionListen(state: ConnectionState, handler: Function) {
    try {
      return this.pusher.connectionListen(state, handler);
    } catch (error) {
      logger.error('Failed to listen to Pusher connection:', error);
      bugsnagClient?.notify(error);
    }
  }

  unsubscribe(channel: Channel): void {
    try {
      this.pusher.unsubscribe(channel);
    } catch (error) {
      logger.error('Failed to unsubscribe from Pusher:', error);
      bugsnagClient?.notify(error);
    }
  }

  unbind(channel: Channel, event: PusherEventTypes, handler: Function) {
    try {
      this.pusher.unbind(channel, event, handler);
    } catch (error) {
      logger.error('Failed to unbind from Pusher:', error);
      bugsnagClient?.notify(error);
    }
  }

  unbindGlobal(channel: Channel): void {
    try {
      this.pusher.unbindGlobal(channel);
    } catch (error) {
      logger.error('Failed to unbind globally from Pusher:', error);
      bugsnagClient?.notify(error);
    }
  }
}
